/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import clientSide from "~/helpers/clientSide";
import { getQuoteId } from "~/helpers/getQuoteId";
import { setNewRelicCustomAttribute } from "~/helpers/newrelic";
import { getOrCreateProcessId } from "../cartFlow";

const getCurrentUrl = () => clientSide && window.location && window.location.href;

export const EVENTS = {
  ElementClicked: "redventures.orderprocessing.v1.ElementClicked",
  ElementViewed: "redventures.orderprocessing.v1.ElementViewed",
  FieldSelected: "redventures.orderprocessing.v1.FieldSelected",
  FieldInputted: "redventures.orderprocessing.v1.FieldInputted",
  FormContinued: "redventures.orderprocessing.v1.FormContinued",
  FormSubmitted: "redventures.orderprocessing.v1.FormSubmitted",
  ProductClicked: "redventures.orderprocessing.v1.ProductClicked",
  ProductViewed: "redventures.ecommerce.v1.ProductViewed",
  PageViewed: "redventures.orderprocessing.v1.PageViewed",
  OrderSessionStarted: "redventures.orderprocessing.v1.OrderSessionStarted",
  ClientErrored: "frontier.clientErrored.v1",
  FormSubmittedV3: "redventures.usertracking.v3.FormSubmitted",
  CspEnforced: "frontier.cspEnforced.v2",
  FlowChanged: "frontier.flowChanged.v1",
  ChatIdentified: "frontier.chatIdentified.v1",
  CustomerCaptured: "redventures.integration.v1alpha1.CustomerCaptured",
  Identify: "core.Identify.v1",
  DeviceMetrics: "frontier.deviceMetrics.v1",
};

const tagularWrapper = (...args) => {
  if (clientSide && tagular) tagular(...args);
};

export const getContext = () => {
  const processId = getOrCreateProcessId();

  const state = clientSide && JSON.parse(window.sessionStorage.getItem("persist:fuel"));
  const instanceId = (clientSide && window._Cohesion?.webContext?.instanceId) || "";
  const manualInstanceId = (clientSide && window._Cohesion?.webContext?.manualInstanceId) || "";

  return {
    anonymousId: state?.tracking?.anonymousId || "",
    environment: process.env.environment === "production" ? "production" : "test",
    instanceId,
    manualInstanceId,
    processId,
    moduleName: "amplify",
    orderSessionId: state?.tracking?.orderSessionId || "",
    orderSessionIdType: "CART",
    pageReferrer: "",
    pageUrl: getCurrentUrl(),
    profileId: "",
    publishId: process.env.release || "",
    scenarioId: "",
    sessionId: state?.tracking?.sessionId || "",
    userAgent: state?.tracking?.userAgent || "",
  };
};

const getClientSessionId = () => {
  const state = clientSide && JSON.parse(window.sessionStorage.getItem("persist:fuel"));
  return state?.tracking?.clientSessionId || state?.tracking?.orderSessionId || "";
};

const getClientId = () => {
  const state = clientSide && JSON.parse(window.sessionStorage.getItem("persist:fuel"));
  return state?.tracking?.clientId || "";
};

export const elementClicked = (data, event) => {
  const { target = {} } = event;
  const payload = {
    "@type": EVENTS.ElementClicked,
    atomContext: getContext(),
    webElement: {
      elementType: data?.elementType || target?.type?.toUpperCase() || "",
      location: data?.location || "",
      position: data?.position || "",
      htmlId: data?.htmlID || target?.id || "",
      text: data?.text || target?.innerText || "",
    },
    actionOutcome: data?.action || "",
    outboundUrl: data?.outboundUrl || "",
  };
  tagularWrapper("beam", payload, true, false);
};

export const chatIdentified = (data) => {
  const payload = {
    "@type": EVENTS.ChatIdentified,
    atomContext: getContext(),
    chatUserId: data?.chatUserId,
  };
  tagularWrapper("beam", payload, true, false);
};

export const elementViewed = (data, event) => {
  const { target = {} } = event;
  const payload = {
    "@type": EVENTS.ElementViewed,
    atomContext: getContext(),
    webElement: {
      elementType: data?.elementType || target?.tageName || "",
      location: data?.location || "",
      position: data?.position || "",
      htmlId: data?.htmlID || target?.id || "",
      text: data?.text || target?.innerText || "",
    },
  };
  tagularWrapper("beam", payload, true, false);
};

export const productClicked = (data) => {
  const payload = {
    "@type": EVENTS.ProductClicked,
    atomContext: getContext(),
    product: {
      brand: data?.brand || "",
      category: data?.category || "",
      coupon: data?.coupon || "",
      location: data?.location || "",
      name: data?.name || "",
      position: data?.position || 0,
      price: parseInt(data?.price || 0, 10),
      productId: data?.productId || "",
      quantity: data?.quantity || 1,
      sku: data?.sku || "",
      subscription: {
        frequency: data?.frequency || "",
      },
      upc: data?.upc || "",
      variant: data?.variant || "",
    },
    actionOutcome: data?.action || "",
  };
  tagularWrapper("beam", payload, true, false);
};

export const productViewed = (data) => {
  const payload = {
    "@type": EVENTS.ProductViewed,
    product: {
      brand: data?.brand || "",
      category: data?.category || "",
      coupon: data?.coupon || "",
      location: data?.location || "",
      name: data?.name || "",
      position: data?.position || 0,
      price: parseInt(data?.price || 0, 10),
      productId: data?.productId || "",
      quantity: data?.quantity || 1,
      sku: data?.sku || "",
      subscription: {
        frequency: data?.frequency || "",
      },
      upc: data?.upc || "",
      variant: data?.variant || "",
    },
  };
  tagularWrapper("beam", payload, true, false);
};

export const fieldSelected = (data, event) => {
  const { target = {} } = event;
  const payload = {
    "@type": EVENTS.FieldSelected,
    atomContext: getContext(),
    userInputField: {
      autofilled: target?.autofilled || data?.autofilled || false,
      fieldType: target?.type || data?.fieldType || "",
      fieldName: target?.name || data?.fieldName || "",
      fieldId: target?.id || data?.fieldId || "",
      fieldLabel: target?.placeholder || data?.fieldLabel || "",
    },
  };
  tagularWrapper("beam", payload, true, false);
};

export const fieldInputted = (data, event) => {
  const { target = {} } = event;
  const payload = {
    "@type": EVENTS.FieldInputted,
    atomContext: getContext(),
    userInputField: {
      autofilled: target?.autofilled || data?.autofilled || false,
      fieldType: target?.type || data?.fieldType || "",
      fieldName: target?.name || data?.fieldName || "",
      fieldId: target?.id || data?.fieldId || "",
      fieldLabel: target?.placeholder || data?.fieldLabel || "",
    },
  };
  tagularWrapper("beam", payload, true, false);
};

export const formSubmitted = (data) => {
  const payload = {
    "@type": EVENTS.FormSubmitted,
    atomContext: getContext(),
    formContext: {
      formType: data?.formType || "",
      formName: data?.formName || "",
      formId: data?.formId || "",
    },
    outboundUrl: data?.outboundUrl || "",
    correlationId: data?.correlationId || "",
    autoFillUsed: data?.autoFillUsed || "",
  };
  tagularWrapper("beam", payload, true, false);
};

export const formSubmittedV3 = (data) => {
  const payload = {
    "@type": EVENTS.FormSubmittedV3,
    formContext: {
      formType: data?.formType || "",
      formName: data?.formName || "",
      formId: data?.formId || "",
    },
  };
  tagularWrapper("beam", payload);
};

export const pageViewed = (autoDecision = false) => {
  const payload = {
    "@type": EVENTS.PageViewed,
    atomContext: getContext(),
  };
  tagularWrapper("beam", payload, true, false);
  tagularWrapper("pageView", false, autoDecision); // The third parameter tells Preamp to not redecision
};

export const pageScrolled = (data) => {
  const payload = {
    "@type": EVENTS.PageScrolled,
    atomContext: getContext(),
    scrollDistance: data?.scrollDistance,
  };
  tagularWrapper("beam", payload, true, false);
};

export const OrderSessionStarted = () => {
  const payload = {
    "@type": EVENTS.OrderSessionStarted,
    atomContext: getContext(),
  };
  tagularWrapper("beam", payload, true, false);
  setNewRelicCustomAttribute([{ name: "orderSessionStartedFired", value: "true" }]);
  deviceMetrics();
};

export const clientErrored = (data) => {
  const payload = {
    "@type": EVENTS.ClientErrored,
    context: getContext(),
    errorMessage: data?.errorMessage || "",
    errorClass: data?.errorClass?.toUpperCase() || "ERROR",
    clientId: getClientId(),
    clientSessionId: getClientSessionId(),
    quoteId: getQuoteId(),
  };
  tagularWrapper("beam", payload, true, false);
};

export const editPlan = (data, event) => {
  const { target = {} } = event;
  const payload = {
    "@type": EVENTS.ElementClicked,
    atomContext: getContext(),
    webElement: {
      text: data?.text || target?.innerText || "",
      location: data?.location || "",
    },
  };
  tagularWrapper("beam", payload, true, false);
};

export const cspEnforced = (data) => {
  const payload = {
    "@type": EVENTS.CspEnforced,
    atomContext: getContext(),
    errorMessage: data?.errorMessage || "",
    offendingSource: data?.offendingSource || "",
    violatedDirective: data?.violatedDirective || "",
    clientId: getClientId(),
    clientSessionId: getClientSessionId(),
  };
  tagularWrapper("beam", payload, true, false);
};

export const flowChanged = (data) => {
  const payload = {
    "@type": EVENTS.FlowChanged,
    atomContext: getContext(),
    prevBuyFlow: data?.prevBuyFlow || "",
    newBuyFlow: data?.newBuyFlow || "",
  };
  tagularWrapper("beam", payload, true, false);
};

/**
 * @function customerCaptured
 * @description This event is used to capture customer information like email address
 */
export const customerCaptured = (data) => {
  const payload = {
    "@type": EVENTS.CustomerCaptured,
    activityDatetime: new Date().toISOString(),
    emailAddress: data?.emailAddress,
    integrationContext: {
      salesflow: "SITE",
    },
  };

  /*
   The fourth parameter set to false suppresses webContext from being
   attached to the event payload (which beam does OTB). Otherwise, the
   request will error because webContext is not a part of the event schema.
  */
  tagularWrapper("beam", payload, false, false);
};

/**
 * @function identify
 * @description This event is used to collect customer identifiers used
 * to build Make DNA profiles
 */
export const identify = (data) => {
  const payload = {
    "@type": EVENTS.Identify,
    traits: {
      email: data?.emailAddress,
    },
  };

  tagularWrapper("beam", payload);
};

/**
 * @function deviceMetrics
 * @description This event is used to capture device metrics including viewport dimensions and screen orientation
 */
export const deviceMetrics = (data = {}) => {
  const deviceData = {
    viewportWidth: data?.viewportWidth ?? (clientSide ? window.innerWidth : 0),
    viewportHeight: data?.viewportHeight ?? (clientSide ? window.innerHeight : 0),
    screenOrientationDetected: data?.screenOrientationDetected ?? (clientSide ? getScreenOrientation() : ""),
  };

  const payload = {
    "@type": EVENTS.DeviceMetrics,
    ...deviceData,
  };
  tagularWrapper("beam", payload, false, true);
};

/**
 * @function getScreenOrientation
 * @description Helper function to detect screen orientation
 */
const getScreenOrientation = () => {
  if (!clientSide) return "";

  if (window.screen && window.screen.orientation) {
    return window.screen.orientation.type || "";
  }

  if (window.innerWidth && window.innerHeight) {
    return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
  }

  return "";
};
